import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, ChevronLeft, ChevronRight, Download, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { StatusPill } from "../components/StatusPill";
import ContentForm from "../components/ContentForm";
import { ContentImportDialog } from "../components/ContentImportDialog";
import { useAuth } from "../context/AuthContext";
import { hasSocial } from "../lib/services";

const STATUSES = ["Draft","Pending Approval","Approved","Scheduled","Published","Changes Required"];

function ownerOf(c) {
  const t = (c.content_type || "").toLowerCase();
  if (t.includes("reel") || t.includes("video") || t.includes("short")) return c.assigned_video_editor || c.assigned_designer || c.assigned_copywriter;
  if (t.includes("blog") || t.includes("linkedin") || t.includes("facebook") || t.includes("post")) return c.assigned_copywriter || c.assigned_designer || c.assigned_video_editor;
  return c.assigned_designer || c.assigned_copywriter || c.assigned_video_editor;
}

const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isoLocal = (d) => `${monthKeyOf(d)}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const [contents, setContents] = useState([]);
  const [clients, setClients] = useState([]);
  const [params, setParams] = useSearchParams();
  const clientFilter = params.get("client") || "all";
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();
  const canImport = !["client", "sales_head", "sales_member"].includes(user?.role);

  const load = async () => {
    const [c, ct] = await Promise.all([api.get("/clients"), api.get("/contents")]);
    setClients(c.data); setContents(ct.data);
  };
  useEffect(()=>{ load(); }, []);

  const filtered = useMemo(() => contents.filter(c =>
    (clientFilter==="all" || c.client_id===clientFilter) &&
    (platform==="all" || c.platform===platform) &&
    (status==="all" || c.status===status)
  ), [contents, clientFilter, platform, status]);

  const monthContents = filtered.filter(c => c.publishing_date?.startsWith(monthKeyOf(cursor)));

  const setClientFilter = (v) => {
    if (v==="all") params.delete("client"); else params.set("client", v);
    setParams(params, { replace: true });
  };

  const changeStatus = async (id, s) => {
    await api.put(`/contents/${id}`, { status: s });
    load();
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1700px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Publishing Ops</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Content Calendar</h1>
          <p className="text-neutral-600 text-sm mt-1">{monthContents.length} pieces this month</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canImport && (
            <Button variant="outline" onClick={()=>setImportOpen(true)} data-testid="import-content-button">
              <FileSpreadsheet className="w-4 h-4 mr-1"/> Import from Excel
            </Button>
          )}
          <Button onClick={()=>{ setEdit(null); setOpen(true); }} data-testid="create-content-button" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1"/> New Content
          </Button>
        </div>
      </div>
      <ContentImportDialog open={importOpen} onOpenChange={setImportOpen}
        onImported={(d)=>{ load(); if (d?.first_date) setCursor(new Date(`${d.first_date}T00:00:00`)); }} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-56 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="filter-client"><SelectValue placeholder="Client"/></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
            <SelectItem value="all">All Clients</SelectItem>
            {clients.filter(hasSocial).map(c=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-40 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="filter-platform"><SelectValue placeholder="Platform"/></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
            {["all","Instagram","Facebook","LinkedIn","YouTube","Meta Ads","Website"].map(p=><SelectItem key={p} value={p}>{p==="all"?"All Platforms":p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="month">
        <TabsList className="bg-[#FFFFFF] border border-[#E4E7EC]">
          <TabsTrigger value="month" data-testid="tab-month">Monthly</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week">Weekly</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
          <TabsTrigger value="sheet" data-testid="tab-sheet">Sheet</TabsTrigger>
        </TabsList>

        <TabsContent value="month">
          <MonthGrid cursor={cursor} setCursor={setCursor} items={filtered} clients={clients} onOpen={(c)=>{ setEdit(c); setOpen(true); }}/>
        </TabsContent>

        <TabsContent value="week">
          <WeekView cursor={cursor} setCursor={setCursor} items={filtered} clients={clients} onOpen={(c)=>{ setEdit(c); setOpen(true); }}/>
        </TabsContent>

        <TabsContent value="sheet">
          <SheetView items={filtered} clients={clients} filters={{ client: clientFilter, platform, status }} onOpen={(c)=>{ setEdit(c); setOpen(true); }}/>
        </TabsContent>

        <TabsContent value="list">
          <div className="panel overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-neutral-500 border-b border-[#E4E7EC] bg-[#FFFFFF]">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Client</div>
              <div className="col-span-3">Content</div>
              <div className="col-span-2">Platform</div>
              <div className="col-span-2">Owner</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            <div className="divide-y divide-[#E4E7EC]">
              {filtered.length===0 && <div className="p-6 text-neutral-500 text-sm">No content matches your filters.</div>}
              {filtered.sort((a,b)=>(b.publishing_date+"").localeCompare(a.publishing_date+"")).map((c) => {
                const cl = clients.find(x=>x.id===c.client_id);
                return (
                  <button key={c.id} onClick={()=>{ setEdit(c); setOpen(true); }} className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-white/[0.02]" data-testid={`list-item-${c.id}`}>
                    <div className="col-span-2 text-[11px] font-mono text-neutral-700">
                      <div>{c.publishing_date || "—"}</div>
                      <div className="text-neutral-500">{c.publishing_time}</div>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center text-[10px] font-mono shrink-0">{(cl?.brand_name||"?").slice(0,2).toUpperCase()}</div>
                      <div className="text-sm truncate">{cl?.brand_name || "—"}</div>
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-sm font-medium truncate">{c.content_topic || c.content_type}</div>
                      <div className="text-[10px] font-mono text-neutral-500 uppercase truncate">{c.content_pillar}</div>
                    </div>
                    <div className="col-span-2 text-[11px] font-mono text-neutral-700">
                      <div>{c.platform}</div>
                      <div className="text-neutral-500">{c.content_type}</div>
                    </div>
                    <div className="col-span-2 text-[11px] font-mono text-neutral-700 truncate" data-testid={`list-owner-${c.id}`}>{ownerOf(c) || "—"}</div>
                    <div className="col-span-1 flex justify-end"><StatusPill status={c.status}/></div>
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ContentForm open={open} onOpenChange={setOpen} initial={edit} clients={clients} onSaved={(d)=>{ load(); if (d?.publishing_date) setCursor(new Date(`${d.publishing_date}T00:00:00`)); }}/>
    </div>
  );
}

const SHEET_COLS = ["Date","Day","Personas","Topical/bucket","Format","Visual Direction / Content idea","Image","Copy","Captions","Content Type","Status","Platform"];

const SHEET_STATUS_TINT = {
  "Draft": "bg-neutral-100 text-neutral-700",
  "Pending Approval": "bg-amber-100 text-amber-800",
  "Approved": "bg-emerald-100 text-emerald-800",
  "Scheduled": "bg-blue-100 text-blue-800",
  "Published": "bg-violet-100 text-violet-800",
  "Changes Required": "bg-rose-100 text-rose-800",
};

function sheetFormatOf(c) {
  const t = (c.content_type || "").toLowerCase();
  if (t.includes("reel") || t.includes("video") || t.includes("short")) return "Video";
  if (t.includes("carousel")) return "Carousel";
  if (t.includes("story")) return "Story";
  if (t.includes("blog")) return "Article";
  return "Image";
}

function dayOf(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long" });
}

function SheetView({ items, clients, filters, onOpen }) {
  const rows = useMemo(() =>
    [...items].sort((a, b) => (a.publishing_date || "").localeCompare(b.publishing_date || "")),
  [items]);

  const slugOf = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const exportName = (ext) => {
    const parts = ["content-calendar"];
    if (filters.client !== "all") {
      const cl = clients.find(x => x.id === filters.client);
      parts.push(slugOf(cl?.brand_name || cl?.client_name || "client"));
    }
    if (filters.platform !== "all") parts.push(slugOf(filters.platform));
    if (filters.status !== "all") parts.push(slugOf(filters.status));
    parts.push(new Date().toISOString().slice(0, 10));
    return `${parts.filter(Boolean).join("-")}.${ext}`;
  };

  const buildTable = () => rows.map(c => {
    const cl = clients.find(x => x.id === c.client_id);
    return {
      "Date": c.publishing_date || "",
      "Day": dayOf(c.publishing_date),
      "Personas": c.personas || "",
      "Topical/bucket": c.content_pillar || "",
      "Format": sheetFormatOf(c),
      "Visual Direction / Content idea": [c.content_topic, cl?.brand_name || cl?.client_name].filter(Boolean).join(" — "),
      "Image": c.creative_url || c.reel_url || "",
      "Copy": c.copy || "",
      "Captions": c.caption || "",
      "Content Type": c.content_type || "",
      "Status": c.status || "",
      "Platform": c.platform || "",
    };
  });

  const exportCSV = () => {
    const data = buildTable();
    if (data.length === 0) { return; }
    const header = SHEET_COLS.join(",");
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = data.map(r => SHEET_COLS.map(c => escape(r[c])).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = exportName("csv");
    a.click(); URL.revokeObjectURL(url);
  };

  const exportXLSX = () => {
    const data = buildTable();
    if (data.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(data, { header: SHEET_COLS });
    ws["!cols"] = [
      { wch: 12 }, { wch: 10 }, { wch: 22 }, { wch: 18 }, { wch: 12 },
      { wch: 34 }, { wch: 36 }, { wch: 30 }, { wch: 40 }, { wch: 16 },
      { wch: 18 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Content Calendar");
    XLSX.writeFile(wb, exportName("xlsx"));
  };

  return (
    <div className="space-y-3" data-testid="sheet-view">
      <div className="flex items-center justify-end gap-2">
        <span className="text-[11px] font-mono text-neutral-500 mr-auto">{rows.length} rows</span>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={rows.length === 0} data-testid="sheet-export-csv" className="border-[#E4E7EC]">
          <Download className="w-3.5 h-3.5 mr-1"/> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={exportXLSX} disabled={rows.length === 0} data-testid="sheet-export-xlsx" className="border-[#E4E7EC] bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
          <Download className="w-3.5 h-3.5 mr-1"/> XLSX
        </Button>
      </div>
      <div className="panel overflow-x-auto">
      <table className="w-full text-xs border-collapse min-w-[1400px]">
        <thead>
          <tr>
            {SHEET_COLS.map((h, i) => (
              <th key={h} data-testid={`sheet-head-${i}`} className="bg-neutral-100 text-neutral-600 text-[10px] font-mono uppercase tracking-wider font-semibold text-left px-3 py-2.5 border border-[#E4E7EC] whitespace-nowrap sticky top-0 z-10">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={SHEET_COLS.length} className="p-8 text-center text-neutral-500 border border-[#E4E7EC]">No content matches your filters.</td></tr>
          )}
          {rows.map((c, ri) => {
            const cl = clients.find(x => x.id === c.client_id);
            return (
              <tr key={c.id} onClick={() => onOpen(c)} className="hover:bg-orange-50/40 cursor-pointer transition-colors" data-testid={`sheet-row-${c.id}`}>
                <td className="px-3 py-2 border border-[#E4E7EC] font-mono text-[11px] whitespace-nowrap">{c.publishing_date || "—"}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] whitespace-nowrap">{dayOf(c.publishing_date)}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] min-w-[120px]">{c.personas || "—"}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] whitespace-nowrap">{c.content_pillar || "—"}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] whitespace-nowrap font-mono text-[11px]">{sheetFormatOf(c)}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] min-w-[220px] max-w-[280px]">
                  <div className="truncate" title={c.content_topic}>{c.content_topic || "—"}</div>
                  {clientFilterless(cl) && <div className="text-[10px] font-mono text-neutral-500">{cl.brand_name || cl.client_name}</div>}
                </td>
                <td className="px-3 py-2 border border-[#E4E7EC]">
                  {c.creative_url || c.reel_url ? (
                    <img src={fileUrl(c.creative_url || c.reel_url)} alt="" className="w-12 h-12 object-cover rounded border border-[#E4E7EC]" data-testid={`sheet-img-${c.id}`}/>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-3 py-2 border border-[#E4E7EC] min-w-[160px] max-w-[220px]"><div className="line-clamp-2" title={c.copy}>{c.copy || "—"}</div></td>
                <td className="px-3 py-2 border border-[#E4E7EC] min-w-[200px] max-w-[260px]"><div className="line-clamp-2" title={c.caption}>{c.caption || "—"}</div></td>
                <td className="px-3 py-2 border border-[#E4E7EC] whitespace-nowrap">{c.content_type || "—"}</td>
                <td className={`px-3 py-2 border border-[#E4E7EC] whitespace-nowrap font-semibold ${SHEET_STATUS_TINT[c.status] || ""}`} data-testid={`sheet-status-${c.id}`}>{c.status}</td>
                <td className="px-3 py-2 border border-[#E4E7EC] whitespace-nowrap">{c.platform || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function clientFilterless(cl) { return !!cl; }


function MonthGrid({cursor, setCursor, items, clients, onOpen}) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth()+1, 0).getDate();
  const cells = [];
  for (let i=0; i<startDow; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const monthKey = monthKeyOf(cursor);
  const byDay = {};
  items.forEach(c => {
    if (c.publishing_date?.startsWith(monthKey)) {
      const day = parseInt(c.publishing_date.slice(8,10),10);
      byDay[day] ||= [];
      byDay[day].push(c);
    }
  });
  const monthName = cursor.toLocaleDateString(undefined, {month:"long", year:"numeric"});

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg tracking-tight">{monthName}</h3>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4"/></Button>
          <Button variant="ghost" size="sm" onClick={()=>setCursor(new Date())} data-testid="calendar-today" className="font-mono text-[11px] uppercase">Today</Button>
          <Button variant="ghost" size="icon" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4"/></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#E4E7EC] rounded-lg overflow-hidden">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
          <div key={d} className="bg-[#FFFFFF] p-2 text-[10px] font-mono uppercase tracking-wider text-neutral-500 text-center">{d}</div>
        ))}
        {cells.map((d,i) => (
          <div key={i} className={`bg-[#FFFFFF] min-h-[100px] p-2 relative ${d?"":"opacity-40"}`} data-testid={d?`day-cell-${d}`:undefined}>
            <div className="text-[11px] font-mono text-neutral-500">{d||""}</div>
            <div className="mt-1 space-y-1">
              {(byDay[d]||[]).slice(0,3).map(c => {
                const cl = clients.find(x=>x.id===c.client_id);
                return (
                  <button key={c.id} onClick={()=>onOpen(c)} title={c.content_topic} className="w-full text-left text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#E4E7EC] hover:border-orange-500/40 bg-[#FFFFFF] truncate block">
                    {c.publishing_time && <span className="text-neutral-600">{c.publishing_time} </span>}<span className="text-neutral-800">{c.content_topic || cl?.brand_name || cl?.client_name || "Untitled"}</span>
                  </button>
                );
              })}
              {(byDay[d]?.length||0) > 3 && <div className="text-[10px] font-mono text-orange-400">+{byDay[d].length-3} more</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekView({ cursor, setCursor, items, clients, onOpen }) {
  // Compute the Sunday of the current week from cursor
  const start = new Date(cursor);
  start.setDate(start.getDate() - start.getDay());
  start.setHours(0,0,0,0);
  const days = Array.from({length:7}, (_,i)=> {
    const d = new Date(start); d.setDate(start.getDate()+i); return d;
  });
  const iso = isoLocal;
  const byDate = {};
  items.forEach(c => { if (c.publishing_date) { (byDate[c.publishing_date] ||= []).push(c); } });
  const today = new Date(); today.setHours(0,0,0,0);
  const label = `${days[0].toLocaleDateString(undefined,{month:"short", day:"numeric"})} – ${days[6].toLocaleDateString(undefined,{month:"short", day:"numeric", year:"numeric"})}`;

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg tracking-tight">{label}</h3>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={()=>{const n=new Date(cursor); n.setDate(n.getDate()-7); setCursor(n);}} data-testid="calendar-prev-week"><ChevronLeft className="w-4 h-4"/></Button>
          <Button variant="ghost" size="sm" onClick={()=>setCursor(new Date())} data-testid="calendar-this-week" className="font-mono text-[11px] uppercase">This week</Button>
          <Button variant="ghost" size="icon" onClick={()=>{const n=new Date(cursor); n.setDate(n.getDate()+7); setCursor(n);}} data-testid="calendar-next-week"><ChevronRight className="w-4 h-4"/></Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d,idx)=> {
          const dayItems = (byDate[iso(d)] || []).sort((a,b)=>(a.publishing_time||"").localeCompare(b.publishing_time||""));
          const isToday = d.getTime() === today.getTime();
          return (
            <div key={idx} className={`rounded-lg border ${isToday?"border-orange-500/50 bg-orange-500/5":"border-[#E4E7EC] bg-[#FFFFFF]"} p-3 min-h-[220px]`} data-testid={`week-day-${iso(d)}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
                <div className={`text-lg font-bold ${isToday?"text-orange-700":"text-neutral-800"}`}>{d.getDate()}</div>
              </div>
              <div className="mt-3 space-y-2">
                {dayItems.length===0 && <div className="text-[10px] font-mono text-neutral-500 italic">—</div>}
                {dayItems.map(c=> {
                  const cl = clients.find(x=>x.id===c.client_id);
                  return (
                    <button key={c.id} onClick={()=>onOpen(c)} className="w-full text-left rounded-md border border-[#E4E7EC] bg-[#FFFFFF] hover:border-orange-500/40 p-2" data-testid={`week-card-${c.id}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[10px] font-mono text-neutral-600">{c.publishing_time || "—"}</span>
                        <StatusPill status={c.status}/>
                      </div>
                      <div className="text-xs font-semibold mt-1 line-clamp-2">{c.content_topic || c.content_type}</div>
                      <div className="text-[10px] font-mono text-neutral-500 uppercase truncate mt-1">{cl?.brand_name || cl?.client_name} · {c.platform}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

